import { useEffect, useRef } from 'react';

interface UseGFActionOptions {
	actionName: string;
	callback: (...args: any[]) => void;
}

/**
 * Generic hook to listen to Gravity Forms actions with proper cleanup.
 * Uses unique tags for each hook instance to avoid conflicts.
 */
export function useGFAction({ actionName, callback }: UseGFActionOptions) {
	const callbackRef = useRef(callback);
	const tagRef = useRef<string>();

	// Keep callback ref up to date
	useEffect(() => {
		callbackRef.current = callback;
	}, [callback]);

	useEffect(() => {
		if (!window.gform?.addAction) {
			return;
		}

		// Generate unique tag for this hook instance
		const tag = `gpb_${actionName}_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
		tagRef.current = tag;

		// Create wrapper function that uses current callback ref
		const wrapper = (...args: any[]) => {
			callbackRef.current(...args);
		};

		// Register directly with Gravity Forms
		window.gform.addAction(actionName, wrapper, 10, tag);

		// Cleanup function
		return () => {
			if (window.gform?.removeAction && tagRef.current) {
				window.gform.removeAction(actionName, tagRef.current);
			}
		};
	}, [actionName]);
}
